import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "./Reveal";
import { journalPage } from "../mock";

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII"];

const FILTER_DESCRIPTIONS = {
  "Places & Journeys": "Dispatches from the destinations we have travelled to serve.",
  "From the Kitchen": "Quick moments from the pass — a dish, a flourish, a flame.",
  "Culinary Notes": "Longer reflections on how we cook, and why.",
  "From the House": "Observations on hospitality and the people behind it.",
  "Celebrations and Memories": "The occasions we have been welcomed into.",
};

const Journal = () => {
  const { eyebrow, heading, intro } = journalPage;
  const entries = [...journalPage.entries].sort((a, b) =>
    (b.date || "").localeCompare(a.date || "")
  );
  const PRIMARY_CATEGORY = "Celebrations and Memories";
  const uniqueCategories = Array.from(new Set(entries.map((e) => e.category)));
  const orderedCategories = [
    ...(uniqueCategories.includes(PRIMARY_CATEGORY) ? [PRIMARY_CATEGORY] : []),
    ...uniqueCategories.filter((c) => c !== PRIMARY_CATEGORY),
  ];
  const filters = ["All", ...orderedCategories];
  const [activeFilter, setActiveFilter] = useState(
    uniqueCategories.includes(PRIMARY_CATEGORY) ? PRIMARY_CATEGORY : "All"
  );
  const visibleEntries =
    activeFilter === "All"
      ? entries
      : entries.filter((e) => e.category === activeFilter);
  return (
    <main className="flex-grow pt-32 pb-24 px-margin-mobile md:px-margin-desktop bg-background-ivory">
      {/* Hero */}
      <section className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
        <Reveal
          as="p"
          className="font-inter text-eyebrow text-brand-secondary uppercase tracking-[0.28em] mb-6"
        >
          {eyebrow}
        </Reveal>
        <Reveal
          as="h1"
          delay={100}
          className="font-cormorant text-display-lg-mobile md:text-display-lg text-brand-ink mb-8"
        >
          {heading}
        </Reveal>
        <Reveal delay={200} className="w-16 h-px bg-brand-gold mx-auto mb-8" />
        <Reveal
          as="p"
          delay={300}
          className="font-cormorant text-body-lg text-deep-taupe"
        >
          {intro}
        </Reveal>
      </section>

      {/* Categories */}
      <section className="max-w-5xl mx-auto mb-14">
        <div className="flex flex-wrap justify-center gap-x-7 gap-y-2">
          {filters.map((cat) => {
            const active = cat === activeFilter;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveFilter(cat)}
                data-testid={`journal-filter-${cat.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`font-inter text-eyebrow uppercase tracking-widest py-2 border-b transition-colors duration-300 ${
                  active
                    ? "text-brand-ink border-brand-gold"
                    : "text-brand-secondary border-transparent hover:text-brand-ink"
                }`}
              >
                {cat}
              </button>
            );
          })}
        </div>
        {FILTER_DESCRIPTIONS[activeFilter] && (
          <p
            key={activeFilter}
            data-testid="journal-filter-description"
            className="mt-5 text-center font-cormorant italic text-body-md text-deep-taupe animate-fade-in"
          >
            {FILTER_DESCRIPTIONS[activeFilter]}
          </p>
        )}
      </section>

      {/* Entries */}
      <section className="max-w-5xl mx-auto" data-testid="journal-entries">
        <div className="w-full h-px bg-hairline-beige" />
        {visibleEntries.map((e, i) => (
          <Reveal key={e.slug} delay={i * 100}>
            <Link
              to={`/journal/${e.slug}`}
              data-testid={`journal-entry-${i}`}
              className="group grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-8 py-12 border-b border-hairline-beige items-center"
            >
              <div className="md:col-span-5 overflow-hidden">
                <div className="w-full aspect-[4/3] overflow-hidden bg-cover-ivory">
                  <img
                    src={e.heroImage}
                    alt={e.title}
                    loading="lazy"
                    className={`w-full h-full object-cover ${e.imgPosition || ""} transition-transform duration-[1200ms] ease-out group-hover:scale-105`}
                    data-testid={`journal-entry-thumb-${i}`}
                  />
                </div>
              </div>
              <div className="md:col-span-7">
                <p className="font-cormorant text-[26px] text-brand-gold/70 leading-none mb-3">
                  {ROMAN[i] || i + 1}
                </p>
                <p className="font-inter text-eyebrow uppercase tracking-widest text-brand-secondary mb-3">
                  {e.category}
                  {e.kicker ? ` · ${e.kicker}` : ""}
                </p>
                <h2 className="font-cormorant text-headline-md text-brand-ink mb-4 transition-colors duration-300 group-hover:text-brand-gold">
                  {e.title}
                </h2>
                <p className="font-cormorant text-body-md text-deep-taupe max-w-2xl mb-5">
                  {e.excerpt}
                </p>
                <span className="inline-flex items-center gap-2 font-inter text-label-sm uppercase tracking-widest text-brand-ink">
                  Read the note
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:text-brand-gold" />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </section>
    </main>
  );
};

export default Journal;
